import { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAirframes } from "../hooks/useAirframes";
import { useAirframeFilter } from "../hooks/useAirframeFilter";
import { useAirframeForm } from "../hooks/useAirframeForm";
import { useComponentAttach } from "../hooks/useComponentAttach";
import { useSubAssemblies } from "../../sub-assemblies/hooks/useSubAssemblies";
import { AirframeList } from "./AirframeList";
import { AttachComponentDialog } from "../dialogs/AttachComponentDialog";
import { AirframeFormDialog } from "../dialogs/AirframeFormDialog";
import { ReplaceComponentDialog } from "../dialogs/ReplaceComponentDialog";
import { MiniPackAirframeDrawer } from "../dialogs/MiniPackAirframeDrawer";
import { ConfirmDialog } from "../../../common/confirm-dialog";
import ManagePackageFilter from "./airframe-filter";
import { OffWingType } from "../../../../types/DetachComponent";
import { useShopVisitReasonService } from "../../../../api/shopVisitReasonService";
import { ShopVisitReasonComponentType, ShopVisitReasonItem } from "../../../../types/shopVisitReason";
import { Airframe } from "../../../../types/airframe";
import { useOperatorService } from "../../../../api/operatorService";
import { useTenantDisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import type { Operator } from "../../../../types/operator";

export function ManageAirframes() {
  const navigate = useNavigate();
  const afm = useAirframes();
  const form = useAirframeForm();
  const { subAssemblies } = useSubAssemblies();
  const { getReasons } = useShopVisitReasonService();
  const [detachReasonOptions, setDetachReasonOptions] = useState<ShopVisitReasonItem[]>([]);
  const [miniPackAirframe, setMiniPackAirframe] = useState<Airframe | null>(null);

  // Operator filter
  const { getOperators } = useOperatorService();
  const [operators, setOperators] = useState<Operator[]>([]);
  const { preferences: displayPreferences } = useTenantDisplayPreferences();
  const hasAssetOperatorPermission = useHasPermission("assets-operator", "read");
  const showOperatorFilter = displayPreferences.showOperator && hasAssetOperatorPermission;
  const filter = useAirframeFilter(afm.airframes);

  useEffect(() => {
    if (!showOperatorFilter) return;
    let active = true;
    void getOperators(true)
      .then((items) => {
        if (active) setOperators(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (active) setOperators([]);
      });
    return () => {
      active = false;
    };
  }, [showOperatorFilter, getOperators]);

 
  const attach = useComponentAttach(afm.components, afm.airframes, afm.reload);

  const detachComponentType = useMemo<ShopVisitReasonComponentType | null>(() => {
    if (attach.confirmDetach.componentType) {
      return attach.confirmDetach.componentType;
    }

    const component = afm.components.find((c) => c.id === attach.confirmDetach.componentId);
    if (!component) return null;

    const rawType = String(component.type || component.componentType || "").trim();
    if (rawType === "Engine" || rawType === "APU" || rawType === "LandingGear") {
      return rawType;
    }
    if (rawType === "Landing Gear" || rawType === "2") {
      return "LandingGear";
    }
    if (rawType === "0") {
      return "Engine";
    }
    if (rawType === "1") {
      return "APU";
    }
    return null;
  }, [afm.components, attach.confirmDetach.componentId, attach.confirmDetach.componentType]);

  useEffect(() => {
    const shouldLoadReasons =
      attach.confirmDetach.open &&
      attach.confirmDetach.step === "offWing" &&
      attach.confirmDetach.offWingType === "In Shop" &&
      !!detachComponentType;

    if (!shouldLoadReasons) {
      setDetachReasonOptions([]);
      return;
    }

    let active = true;
    void getReasons(detachComponentType)
      .then((reasons) => {
        if (active) {
          setDetachReasonOptions(Array.isArray(reasons) ? reasons : []);
        }
      })
      .catch(() => {
        if (active) {
          setDetachReasonOptions([]);
        }
      });

    return () => {
      active = false;
    };
  }, [attach.confirmDetach.open, attach.confirmDetach.step, attach.confirmDetach.offWingType, detachComponentType, getReasons]);

  return (
    <Box sx={{ height: "100%", p: { xs: 0, md: 2 } }}>
      <Grid container spacing={2} sx={{ p: { xs: 0, md: 2 } }}>
        {/* LEFT FILTER */}
        <Grid size={{ xs: 12, md: 3 }}>
          <ManagePackageFilter
            freeComponent={attach.free}
            onCreateClick={form.startCreate}
            operators={operators}
            operatorFilters={filter.operatorFilters}
            onOperatorFilterToggle={filter.toggleOperatorFilter}
            showOperatorFilter={showOperatorFilter}
          />
        </Grid>

        {/* MAIN LIST */}
        <Grid size={{ xs: 12, md: 9 }}>
          <AirframeList
            airframes={filter.filteredAirframes}
            displayPreferences={displayPreferences}
            components={afm.components}
            subAssemblies={subAssemblies}
            loading={afm.loading}
            attached={attach.attached}
            getInstalledComponentsForAirframe={attach.getInstalledComponentsForAirframe}
            getComponentDisplayName={attach.getComponentDisplayName}
            onAttach={attach.openAttachDialog}
            onReplace={attach.openReplaceDialog}
            onDetach={attach.detach}
            onEdit={form.startEdit}
            onDelete={afm.deletePkg}
            onMiniPack={setMiniPackAirframe}
          />
        </Grid>
      </Grid>

      {/* PACKAGE CREATE / EDIT */}
      <AirframeFormDialog
        {...form}
        onSave={afm.saveAirframe}
      />

      {/* ATTACH COMPONENT */}
      <AttachComponentDialog
        open={attach.openAttach}
        airframe={attach.selectedPkg}
        components={attach.attachableComponents}
        form={attach.form}
        onChange={attach.setForm}
        subAssemblyByComponentId={attach.subAssemblyByComponentId}
        templateSupportsSubAssemblies={attach.templateSupportsSubAssemblies}
        onClose={() => attach.setOpenAttach(false)}
        onAttach={attach.attach}
        isAttaching={attach.isAttaching}
      />

      {/* REPLACE COMPONENT */}
      <ReplaceComponentDialog
        open={attach.openReplace}
        airframe={attach.selectedPkg}
        replaceTarget={attach.replaceTarget}
        components={attach.componentsToReplace}
        form={attach.form}
        onChange={(f) => attach.setForm({ componentId: f.componentId, slotId: "" })}
        subAssemblyByComponentId={attach.subAssemblyByComponentId}
        subAssemblies={subAssemblies}
        onClose={() => attach.setOpenReplace(false)}
        onReplace={attach.replace}
        isReplacing={attach.isReplacing}
      />

      <MiniPackAirframeDrawer
        open={Boolean(miniPackAirframe)}
        airframe={miniPackAirframe}
        onClose={() => setMiniPackAirframe(null)}
        onOpenJobs={(airframe) => {
          const label = airframe.tailNumber || "Airframe";
          navigate(`/assets/mini-pack-generator?scope=airframe&label=${encodeURIComponent(label)}`);
        }}
      />

      {/* DELETE AIRFRAME CONFIRMATION */}
      <ConfirmDialog
        open={afm.confirmDelete.open}
        title="Delete Airframe"
        message={
          <Box sx={{ mt: 1 }}>
            <Box component="p" sx={{ color: "#ccc", m: 0, lineHeight: 1.7 }}>
              Are you sure you want to delete airframe "{afm.confirmDelete.airframe?.tailNumber}"? All attached assemblies will be deleted and their documents archived with this airframe. This action can't be undone.
            </Box>
            <Box component="p" sx={{ color: "#ccc", m: 0, mt: 1.5, lineHeight: 1.7 }}>
              Deleted documents can be recovered. Contact an admin if you need help restoring archived records.
            </Box>
          </Box>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDangerous={true}
        onConfirm={afm.handleConfirmDelete}
        onCancel={() => afm.setConfirmDelete({ open: false, airframe: null })}
        isLoading={afm.isDeletingAirframe}
      />

      {/* DETACH COMPONENT CONFIRMATION */}
      <ConfirmDialog
        open={attach.confirmDetach.open && attach.confirmDetach.step === "confirm"}
        title="Detach Assembly"
        message={
          attach.confirmDetach.skipOffWingStep
            ? "Are you sure you want to detach this assembly from the airframe? Attached sub-assemblies will remain with the landing gear."
            : "Are you sure you want to detach this assembly from the airframe?"
        }
        confirmLabel="Detach"
        cancelLabel="Cancel"
        isDangerous={true}
        onConfirm={attach.handleConfirmDetach}
        onCancel={() => attach.setConfirmDetach({
          open: false,
          step: "confirm",
          componentId: "",
          offWingType: "In Shop",
          shopVisitReasonId: null,
          shopVisitReasonLabel: null,
          componentType: null,
          skipOffWingStep: false,
        })}
        isLoading={attach.isDetaching}
      />

      {/* OFF-WING TYPE SELECTION */}
      <ConfirmDialog
        open={attach.confirmDetach.open && attach.confirmDetach.step === "offWing"}
        title="Detach Assembly"
        message={
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ color: "#ccc", mb: 2 }}>
              Select the off-wing status for the detached assembly.
            </Typography>
            <FormControl fullWidth required>
              <InputLabel
                id="off-wing-type-label"
                sx={{
                  color: "#fff",
                  "&.Mui-focused": { color: "#fff" },
                  "&.MuiInputLabel-shrink": { color: "#fff" },
                }}
              >
                OffWing Type
              </InputLabel>
              <Select
                labelId="off-wing-type-label"
                label="OffWing Type"
                value={attach.confirmDetach.offWingType}
                onChange={(e) =>
                  attach.setConfirmDetach({
                    ...attach.confirmDetach,
                    offWingType: e.target.value as OffWingType,
                    shopVisitReasonId:
                      e.target.value === "In Shop"
                        ? attach.confirmDetach.shopVisitReasonId
                        : null,
                    shopVisitReasonLabel:
                      e.target.value === "In Shop"
                        ? attach.confirmDetach.shopVisitReasonLabel
                        : null,
                  })
                }
                sx={{
                  color: "#fff",
                  backgroundColor: "#1a1a1a",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#fff",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#fff",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "hsl(19 101% 51% / 1)",
                    borderWidth: 1,
                  },
                  "& .MuiSvgIcon-root": {
                    color: "#fff",
                  },
                }}
              >
                <MenuItem value="In Shop">In Shop</MenuItem>
                <MenuItem value="Serviceable - Spare">Serviceable - Spare</MenuItem>
                <MenuItem value="Unserviceable - Spare">Unserviceable - Spare</MenuItem>
              </Select>
            </FormControl>

            {attach.confirmDetach.offWingType === "In Shop" && (
              <FormControl fullWidth required sx={{ mt: 2 }}>
                <InputLabel
                  id="in-shop-reason-label"
                  sx={{
                    color: "#fff",
                    "&.Mui-focused": { color: "#fff" },
                    "&.MuiInputLabel-shrink": { color: "#fff" },
                  }}
                >
                  In Shop Reason
                </InputLabel>
                <Select
                  labelId="in-shop-reason-label"
                  label="In Shop Reason"
                  value={attach.confirmDetach.shopVisitReasonId || ""}
                  onChange={(e) => {
                    const selectedReason = detachReasonOptions.find((r) => r.id === e.target.value);
                    attach.setConfirmDetach({
                      ...attach.confirmDetach,
                      shopVisitReasonId: String(e.target.value),
                      shopVisitReasonLabel: selectedReason?.label || null,
                    });
                  }}
                  sx={{
                    color: "#fff",
                    backgroundColor: "#1a1a1a",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#fff",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#fff",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "hsl(19 101% 51% / 1)",
                      borderWidth: 1,
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#fff",
                    },
                  }}
                >
                  {detachReasonOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      No reasons configured
                    </MenuItem>
                  )}
                  {detachReasonOptions.map((reason, index) => (
                    <MenuItem key={`${String(reason.id)}-${reason.label}-${index}`} value={reason.id}>
                      {reason.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        }
        confirmLabel="Save"
        cancelLabel="Cancel"
        isDangerous={false}
        onConfirm={attach.handleSaveDetach}
        onCancel={() => attach.setConfirmDetach({
          open: false,
          step: "confirm",
          componentId: "",
          offWingType: "In Shop",
          shopVisitReasonId: null,
          shopVisitReasonLabel: null,
          componentType: null,
          skipOffWingStep: false,
        })}
        isLoading={attach.isDetaching}
      />
    </Box>
  );
}
